import { useCallback, useEffect, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { convertGeneralContextErrorToHookForm } from '@src/common';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector, otpSelector, setErrors } from '@src/features/customers/auth/store/auth';
import { canResendCode, isValidVerificationCode } from '@src/domains/customers';

import { useLoginEmailField } from '../useLoginEmailField';
import { useLogin } from '../useLogin';
import { otpLoginFormSchema, type OtpLoginFormValues } from './otpLoginFormSchema';

type UseOtpLoginFormParams = {
  initialEmail: string;
  active: boolean;
  onEmailChange?: (email: string) => void;
  onCodeSent?: () => void;
  onCodeValidated?: () => void;
};

export const useOtpLoginForm = (params: UseOtpLoginFormParams) => {
  const { initialEmail, active, onEmailChange, onCodeSent, onCodeValidated } = params;
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const contextErrors = useAppSelector(errorsSelector);
  const { loading: otpSending, requestLockedUntil } = useAppSelector(otpSelector);
  const { submitOtp, sendOtp, loading, otpResend } = useLogin();
  const lastSentEmailRef = useRef<string | null>(null);
  const initialOtpAutoSentRef = useRef(false);
  const schema = useMemo(() => otpLoginFormSchema(i18n), [i18n]);

  const {
    control,
    setValue,
    setError,
    clearErrors: clearFormErrors,
    handleSubmit,
    trigger,
    watch,
    formState: { errors },
  } = useForm<OtpLoginFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: { email: initialEmail ?? '', otp: '' },
  });

  const { email, onEmailInputChange } = useLoginEmailField({
    initialEmail,
    onEmailChange,
    setValue,
    watch,
  });
  const otp = watch('otp') ?? '';

  const requestOtp = useCallback(
    async (targetEmail: string) => {
      if (!targetEmail) return;
      lastSentEmailRef.current = targetEmail;

      const sent = await sendOtp(targetEmail);

      if (sent) {
        onCodeSent?.();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sendOtp is rebuilt on every render
    [onCodeSent],
  );

  useEffect(() => {
    if (!active || !email || initialOtpAutoSentRef.current) return;

    const emailValid = schema.shape.email.safeParse(email).success;
    if (!emailValid) return;

    if (lastSentEmailRef.current === email) return;

    const lockActive = requestLockedUntil && !canResendCode(requestLockedUntil);

    if (lockActive) {
      lastSentEmailRef.current = email;
      dispatch(setErrors('otp', {}));
      clearFormErrors('otp');
      initialOtpAutoSentRef.current = true;
      return;
    }

    setValue('otp', '', { shouldValidate: false });
    clearFormErrors('otp');
    requestOtp(email);
    initialOtpAutoSentRef.current = true;
  }, [active, email, setValue, clearFormErrors, requestOtp, schema, requestLockedUntil, dispatch]);

  useEffect(() => {
    convertGeneralContextErrorToHookForm('otp', setError, contextErrors, 'otp');
    convertGeneralContextErrorToHookForm('otp', setError, contextErrors, 'login');
  }, [contextErrors, setError]);

  const isSubmitEnabled = isValidVerificationCode(otp);
  const resendSeconds = otpResend.minutes * 60 + otpResend.seconds;

  const onSubmit = handleSubmit(async (data) => {
    const validated = await submitOtp(data.email, data.otp);

    if (validated) {
      onCodeValidated?.();
    }
  });

  const resendOtp = async () => {
    if (!otpResend.canResend) return;

    const isEmailValid = await trigger('email');
    if (!isEmailValid) return;

    setValue('otp', '');
    clearFormErrors('otp');
    requestOtp(email);
  };

  return {
    control,
    errors,
    loading,
    otpSending,
    isSubmitEnabled,
    email,
    onSubmit,
    resendOtp,
    otpResend,
    resendSeconds,
    requestLockedUntil,
    onEmailInputChange,
    setOtp: (value: string) => setValue('otp', value),
  };
};

export default useOtpLoginForm;
